import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/apiClient";
import type {
  RefundMethod,
  RefundStockQuestion,
  Sale,
  SaleLine,
  SaleRefundLinePayload,
  SaleRefundPreview,
} from "../../api/types";
import { METHOD_LABEL, salePaidText } from "../../utils/salePayments";
import { parseTypedAmount } from "../../utils/typedAmount";
import { Modal } from "../Modal";
import { SegmentedControl } from "../ui/SegmentedControl";

/**
 * G-162 (WO-E part 2; owner's approved mockup, 2026-10-19): the refund
 * window — the same on Sales records and on a handed-over order. Lines are
 * picked, each refunded in full or by a lower amount; an item line's units
 * may go back on the shelf; one method, pre-picked from how the sale was
 * paid. Every figure in it comes from the server's preview (`refund-plan.ts`),
 * so the window never shows what the save would not write. Labels, numbers
 * and pills only.
 */
const eur = (v: string | number) => `€${Number(v).toFixed(2)}`;
/** The amount box shows the Spanish way, as typed: "10,00". */
const typed = (v: string) => Number(v).toFixed(2).replace(".", ",");
const whenText = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

type LineDraft = { checked: boolean; amount: string; restock: boolean; restockQty: string };

function lineName(line: SaleLine): string {
  if (line.kind === "BOUQUET") return line.bouquetBuild?.template?.name ?? "Custom bouquet";
  const qty = Number(line.quantity);
  return `${line.item?.name ?? "Item"}${qty !== 1 ? ` × ${qty}` : ""}`;
}

function lineDetail(line: SaleLine): string {
  const rate = `IVA ${Number(line.vatRatePercent)} %`;
  if (line.kind === "BOUQUET") return `Bouquet · ${rate}`;
  return Number(line.quantity) !== 1 ? `${eur(line.unitPriceEur)} each · ${rate}` : rate;
}

export function RefundSaleModal({
  sale,
  onClose,
  onRefunded,
}: {
  sale: Sale;
  onClose: () => void;
  onRefunded: () => Promise<void>;
}) {
  const [base, setBase] = useState<SaleRefundPreview | null>(null);
  const [preview, setPreview] = useState<SaleRefundPreview | null>(null);
  // Which lines the shown figures were worked out for: the save is offered
  // only while they match what will be sent (review fix — a click inside the
  // preview's delay sent amounts the button did not show).
  const [previewFor, setPreviewFor] = useState<string>("[]");
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [method, setMethod] = useState<RefundMethod | null>(null);
  const [reason, setReason] = useState("");
  // A preview's refusal (the lines asked) and a refused save are kept apart:
  // the save's message goes as soon as the person changes anything.
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  // What is left on each line, the methods and the pre-picked one.
  useEffect(() => {
    let cancelled = false;
    api
      .previewSaleRefund(sale.id, [])
      .then((p) => {
        if (cancelled) return;
        setBase(p);
        setPreview(p);
        setPreviewFor("[]");
        setMethod(p.suggestedMethod);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not open the refund.");
      });
    return () => {
      cancelled = true;
    };
  }, [sale.id]);

  const leftOf = (lineId: string) => base?.lines.find((l) => l.saleLineId === lineId);
  // "N of M" comes from the LATEST preview: M is what the amount typed on the
  // item stands for (owner 2026-10-20), so it moves with the amount.
  const capOf = (lineId: string) =>
    Number(preview?.lines.find((l) => l.saleLineId === lineId)?.restockableQty ?? 0);

  // The lines asked, as the server reads them; an unreadable amount stops the preview.
  const asked = useMemo((): { lines: SaleRefundLinePayload[]; problem: string | null } => {
    const lines: SaleRefundLinePayload[] = [];
    for (const line of sale.lines) {
      const d = drafts[line.id];
      if (!d?.checked) continue;
      const amount = parseTypedAmount(d.amount);
      if (!amount.ok) return { lines: [], problem: `${lineName(line)}: ${amount.message}` };
      const wanted = d.restock ? Number(d.restockQty) : 0;
      if (d.restock && (!Number.isFinite(wanted) || wanted <= 0)) {
        return { lines: [], problem: `${lineName(line)}: how many go back on the shelf?` };
      }
      // Never more than M as the latest preview knows it: the typed number
      // stays (retyping an amount must not lose it), what is SENT follows M.
      // A stale M converges in one more round, and the save waits for it.
      const qty = Math.min(wanted, capOf(line.id));
      lines.push({ saleLineId: line.id, amountEur: amount.value, ...(qty > 0 ? { restockQty: qty } : {}) });
    }
    return { lines, problem: null };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- capOf reads `preview`
  }, [drafts, sale.lines, preview]);
  const askedKey = JSON.stringify(asked.lines);

  // Live figures from the server, a moment after the last change.
  useEffect(() => {
    if (!base) return;
    // Any change makes an answer still in flight stale.
    const mine = ++seq.current;
    if (asked.problem) {
      setError(asked.problem);
      return;
    }
    const key = askedKey;
    const timer = window.setTimeout(() => {
      api
        .previewSaleRefund(sale.id, asked.lines)
        .then((p) => {
          if (mine !== seq.current) return;
          setPreview(p);
          setPreviewFor(key);
          setError(null);
        })
        .catch((err: unknown) => {
          if (mine === seq.current) setError(err instanceof Error ? err.message : "Could not work it out.");
        });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [asked, askedKey, base, sale.id]);

  const setDraft = (lineId: string, patch: Partial<LineDraft>) => {
    setSaveError(null);
    setDrafts((all) => {
      const before = all[lineId] ?? { checked: false, amount: "", restock: false, restockQty: "1" };
      return { ...all, [lineId]: { ...before, ...patch } };
    });
  };

  const toggleLine = (line: SaleLine, checked: boolean) => {
    const left = leftOf(line.id);
    setDraft(line.id, {
      checked,
      amount: checked && left ? typed(left.leftEur) : "",
      restock: false,
      restockQty: "1",
    });
  };

  const total = Number(preview?.totalEur ?? 0);
  const ready =
    !busy &&
    asked.problem === null &&
    asked.lines.length > 0 &&
    previewFor === askedKey &&
    total > 0 &&
    method !== null &&
    reason.trim().length >= 3 &&
    error === null;

  const save = async () => {
    if (!ready || !method) return;
    setBusy(true);
    setSaveError(null);
    try {
      await api.createSaleRefund(sale.id, { lines: asked.lines, method, reason: reason.trim() });
      await onRefunded();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "Could not refund.");
    } finally {
      setBusy(false);
    }
  };

  const restocked = Number(preview?.restockedUnits ?? 0);
  // "1 Candle" when one line goes back, as the mockup reads; else a count.
  const restockedLines = sale.lines.filter((l) => drafts[l.id]?.checked && drafts[l.id]?.restock);
  const restockedText =
    restocked <= 0
      ? "—"
      : restockedLines.length === 1 && restockedLines[0].item
        ? `${restocked} ${restockedLines[0].item.name}`
        : `${restocked} ${restocked === 1 ? "unit" : "units"}`;
  const countsOn = preview
    ? `Today, ${new Date(preview.countsOn).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`
    : "—";

  return (
    <Modal title="Refund a sale" onClose={onClose}>
      <div className="refund-window">
        <p className="muted refund-window-sale">
          {whenText(sale.soldAt)} · {sale.location.name} · {sale.soldBy.fullName} ·{" "}
          {sale.moneyHome === "rows"
            ? `paid ${salePaidText(sale, eur)} ${eur(sale.totalEur)}`
            : `${eur(sale.totalEur)} · ${salePaidText(sale, eur)}`}
        </p>

        <div className="refund-lines" role="group" aria-label="Lines">
          <div className="refund-line refund-line-head" aria-hidden="true">
            <span />
            <span>Line</span>
            <span className="refund-num">Sold</span>
            <span>Refund</span>
            <span>Back on the shelf</span>
          </div>
          {sale.lines.map((line) => {
            const left = leftOf(line.id);
            const d = drafts[line.id];
            const open = left != null && Number(left.leftEur) > 0;
            const restockable = capOf(line.id);
            return (
              <div key={line.id} className={open ? "refund-line" : "refund-line is-closed"}>
                <input
                  type="checkbox"
                  checked={Boolean(d?.checked)}
                  disabled={!open || busy}
                  onChange={(e) => toggleLine(line, e.target.checked)}
                  aria-label={`Refund ${lineName(line)}`}
                />
                <div>
                  <div className="refund-line-name">{lineName(line)}</div>
                  <div className="muted refund-line-detail">
                    {lineDetail(line)}
                    {left && open && Number(left.leftEur) < Number(line.lineTotalEur)
                      ? ` · ${eur(left.leftEur)} left`
                      : ""}
                    {left && !open && Number(line.lineTotalEur) > 0 ? " · refunded" : ""}
                  </div>
                </div>
                <span className="refund-num">{eur(line.lineTotalEur)}</span>
                {d?.checked ? (
                  <label className="refund-amount">
                    <span className="muted">€</span>
                    <input
                      inputMode="decimal"
                      value={d.amount}
                      onChange={(e) => setDraft(line.id, { amount: e.target.value })}
                      aria-label={`Refund amount for ${lineName(line)}`}
                      disabled={busy}
                    />
                  </label>
                ) : (
                  <span className="muted">—</span>
                )}
                {d?.checked && line.kind === "ITEM" && restockable > 0 ? (
                  <label className="refund-restock">
                    <input
                      type="checkbox"
                      checked={d.restock}
                      onChange={(e) =>
                        setDraft(line.id, {
                          restock: e.target.checked,
                          restockQty: String(Math.min(restockable, 1)),
                        })
                      }
                      aria-label={`Back on the shelf: ${lineName(line)}`}
                      disabled={busy}
                    />
                    {d.restock ? (
                      <>
                        <input
                          type="number"
                          min={Math.min(1, restockable)}
                          max={restockable}
                          step="any"
                          value={d.restockQty}
                          onChange={(e) => setDraft(line.id, { restockQty: e.target.value })}
                          aria-label={`Units back on the shelf: ${lineName(line)}`}
                          disabled={busy}
                        />
                        <span>of {restockable}</span>
                      </>
                    ) : null}
                  </label>
                ) : (
                  <span className="muted">—</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="refund-how">
          <div className="field">
            <span className="refund-field-label">Money back by</span>
            <SegmentedControl
              ariaLabel="Money back by"
              value={method}
              onChange={(m) => {
                setMethod(m);
                setSaveError(null);
              }}
              disabled={busy || !base}
              options={(base?.allowedMethods ?? []).map((m) => ({ value: m, label: METHOD_LABEL[m] }))}
            />
          </div>
          <div className="field refund-reason">
            <label htmlFor="refund-reason">Reason</label>
            <input
              id="refund-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setSaveError(null);
              }}
              disabled={busy}
            />
          </div>
        </div>

        <div className="refund-summary" aria-live="polite">
          <div>
            <span className="refund-summary-label">Refund</span>
            <strong>{eur(preview?.totalEur ?? 0)}</strong>
          </div>
          <div>
            <span className="refund-summary-label">IVA inside</span>
            <strong>{eur(preview?.vatEur ?? 0)}</strong>
          </div>
          <div>
            <span className="refund-summary-label">Back on the shelf</span>
            <strong>{restockedText}</strong>
          </div>
          {preview?.commissionEur != null ? (
            <div>
              <span className="refund-summary-label">{preview.seller.fullName}&apos;s commission</span>
              <strong className={Number(preview.commissionEur) > 0 ? "refund-summary-minus" : undefined}>
                {Number(preview.commissionEur) > 0 ? `−${eur(preview.commissionEur)}` : eur(0)}
              </strong>
            </div>
          ) : null}
          <div>
            <span className="refund-summary-label">Counts on</span>
            <strong>{countsOn}</strong>
          </div>
        </div>

        {error ?? saveError ? <div className="alert error">{error ?? saveError}</div> : null}

        <div className="button-row refund-actions">
          <button type="button" className="button plain" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="button" onClick={() => void save()} disabled={!ready}>
            {busy
              ? "Refunding…"
              : `Refund ${eur(total)}${method ? ` by ${METHOD_LABEL[method].toLowerCase()}` : ""}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * G-166 (owner 2026-10-20): the same refund window, opened on a refund made in
 * Shopify to answer its stock question. The refunded lines and their money
 * are filled in and locked — Shopify already gave the money back — and only
 * "Back on the shelf" (N of M) can be ticked. Units ticked go back to their
 * lots at their original cost; "Nothing came back" closes the question too.
 */
export function RefundStockModal({
  sale,
  refundId,
  onClose,
  onSaved,
}: {
  sale: Sale;
  refundId: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [question, setQuestion] = useState<RefundStockQuestion | null>(null);
  const [back, setBack] = useState<Record<string, { on: boolean; qty: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getRefundStockQuestion(refundId)
      .then((q) => {
        if (!cancelled) setQuestion(q);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not open the refund.");
      });
    return () => {
      cancelled = true;
    };
  }, [refundId]);

  const asked = useMemo(() => {
    const lines: Array<{ saleLineId: string; restockQty: number }> = [];
    for (const l of question?.lines ?? []) {
      const b = back[l.saleLineId];
      if (!b?.on) continue;
      const qty = Number(b.qty);
      if (!Number.isFinite(qty) || qty <= 0 || qty > Number(l.restockableQty)) return null;
      lines.push({ saleLineId: l.saleLineId, restockQty: qty });
    }
    return lines;
  }, [back, question]);
  const units = (asked ?? []).reduce((sum, l) => sum + l.restockQty, 0);

  const answer = async (lines: Array<{ saleLineId: string; restockQty: number }>) => {
    setBusy(true);
    setError(null);
    try {
      await api.answerRefundStock(refundId, lines);
      await onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const refundedDay = question
    ? new Date(question.refundedAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })
    : "—";

  return (
    <Modal title="Refund a sale" onClose={onClose}>
      <div className="refund-window">
        <p className="muted refund-window-sale">
          {whenText(sale.soldAt)} · {sale.location.name} · {sale.soldBy.fullName} · {eur(sale.totalEur)} ·{" "}
          {salePaidText(sale, eur)}
        </p>

        <div className="refund-lines" role="group" aria-label="Lines">
          <div className="refund-line refund-line-head" aria-hidden="true">
            <span />
            <span>Line</span>
            <span className="refund-num">Sold</span>
            <span>Refund</span>
            <span>Back on the shelf</span>
          </div>
          {(question?.lines ?? []).map((l) => {
            const line = sale.lines.find((sl) => sl.id === l.saleLineId);
            const restockable = Number(l.restockableQty);
            const b = back[l.saleLineId];
            return (
              <div key={l.saleLineId} className="refund-line">
                <input type="checkbox" checked disabled aria-label="Refunded in Shopify" />
                <div>
                  <div className="refund-line-name">{line ? lineName(line) : "Line"}</div>
                  <div className="muted refund-line-detail">{line ? lineDetail(line) : ""}</div>
                </div>
                <span className="refund-num">{line ? eur(line.lineTotalEur) : "—"}</span>
                <span className="refund-amount is-locked">{eur(l.amountEur)}</span>
                {l.kind === "ITEM" && restockable > 0 ? (
                  <label className="refund-restock">
                    <input
                      type="checkbox"
                      checked={Boolean(b?.on)}
                      onChange={(e) =>
                        setBack((all) => ({
                          ...all,
                          [l.saleLineId]: { on: e.target.checked, qty: String(Math.min(restockable, 1)) },
                        }))
                      }
                      aria-label={`Back on the shelf: ${line ? lineName(line) : "line"}`}
                      disabled={busy || question?.stockCheckedAt != null}
                    />
                    {b?.on ? (
                      <>
                        <input
                          type="number"
                          min={Math.min(1, restockable)}
                          max={restockable}
                          step="any"
                          value={b.qty}
                          onChange={(e) =>
                            setBack((all) => ({ ...all, [l.saleLineId]: { on: true, qty: e.target.value } }))
                          }
                          aria-label={`Units back on the shelf: ${line ? lineName(line) : "line"}`}
                          disabled={busy}
                        />
                        <span>of {restockable}</span>
                      </>
                    ) : null}
                  </label>
                ) : (
                  <span className="muted">—</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="refund-summary" aria-live="polite">
          <div>
            <span className="refund-summary-label">Refund</span>
            <strong>{eur(question?.totalEur ?? 0)}</strong>
          </div>
          <div>
            <span className="refund-summary-label">Money back by</span>
            <strong>Shopify</strong>
          </div>
          <div>
            <span className="refund-summary-label">Back on the shelf</span>
            <strong>{units > 0 ? `${units} ${units === 1 ? "unit" : "units"}` : "—"}</strong>
          </div>
          <div>
            <span className="refund-summary-label">Counts on</span>
            <strong>{refundedDay}</strong>
          </div>
        </div>

        {error ? <div className="alert error">{error}</div> : null}

        <div className="button-row refund-actions">
          <button type="button" className="button plain" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="button tinted"
            onClick={() => void answer([])}
            disabled={busy || !question || question.stockCheckedAt != null}
          >
            Nothing came back
          </button>
          <button
            type="button"
            className="button"
            onClick={() => asked && void answer(asked)}
            disabled={busy || !question || question.stockCheckedAt != null || asked === null || units <= 0}
          >
            {busy ? "Saving…" : units > 0 ? `Put ${units} back on the shelf` : "Back on the shelf"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
